import React from 'react';
import { Filter, Star, Check, Award, Shield, Loader } from 'lucide-react';
import type { SearchFilters, Service } from '../types';
import { supabase } from '../lib/supabase';

export function SearchFilters({ filters, onFilterChange }: {
  filters: SearchFilters;
  onFilterChange: (filters: SearchFilters) => void;
}) {
  const [services, setServices] = React.useState<Service[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    async function loadServices() {
      try {
        const { data, error } = await supabase
          .from('services')
          .select('id, title')
          .order('title');

        if (error) throw error;
        setServices(data || []);
      } catch (error) {
        console.error('Error loading services:', error);
      } finally {
        setLoading(false);
      }
    }

    loadServices();
  }, []);

  // Ensure filters have default values
  const safeFilters = {
    services: filters.services || [],
    rating: filters.rating,
    verified: filters.verified || false,
    featured: filters.featured || false,
    sortBy: filters.sortBy || ''
  };

  return (
    <aside className="bg-white rounded-lg shadow-md p-6" role="search" aria-label="Filter plumbers">
      <div className="flex items-center mb-4">
        <Filter className="w-5 h-5 text-blue-600 mr-2" />
        <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
      </div>

      <div className="space-y-6">
        {/* Services */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Service Type
              {safeFilters.services.length > 0 && (
                <span className="ml-2 text-sm text-gray-500">
                  ({safeFilters.services.length} selected)
                </span>
              )}
            </label>
            {loading && <Loader className="w-4 h-4 animate-spin text-gray-400" />}
          </div>
          {!loading && (
            <div className="space-y-2">
              {services.map(service => (
                <label key={service.id} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={safeFilters.services.includes(service.id)}
                    onChange={(e) => {
                      const newServices = e.target.checked
                        ? [...safeFilters.services, service.id]
                        : safeFilters.services.filter(id => id !== service.id);
                      onFilterChange({ ...filters, services: newServices });
                    }}
                    aria-label={`Filter by ${service.title}`}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700 hover:text-gray-900">
                    {service.title.replace('24/7 ', '')}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Rating */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Minimum Rating
          </label>
          <div className="flex items-center space-x-2">
            {[1, 2, 3, 4, 5].map(rating => (
              <button
                key={rating}
                onClick={() => onFilterChange({ 
                  ...filters, 
                  rating: safeFilters.rating === rating ? undefined : rating 
                })}
                aria-label={`Filter by ${rating} star${rating !== 1 ? 's' : ''} and up`}
                className={`p-2 rounded-full ${
                  safeFilters.rating === rating
                    ? 'bg-blue-100 text-blue-600'
                    : 'text-gray-400 hover:text-gray-600'
                }`}
                title={`${rating} star${rating !== 1 ? 's' : ''} and up`}
              >
                <Star className={`w-5 h-5 ${
                  safeFilters.rating === rating ? 'fill-current' : ''
                }`} />
              </button>
            ))}
          </div>
        </div>

        {/* Verified & Featured */}
        <div className="space-y-2">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={safeFilters.verified}
              onChange={(e) => onFilterChange({
                ...filters,
                verified: e.target.checked
              })}
              aria-label="Show only verified plumbers"
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700 group-hover:text-gray-900 flex items-center">
              Verified Only
              <Shield className="w-4 h-4 ml-1 text-blue-600" />
            </span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={safeFilters.featured}
              onChange={(e) => onFilterChange({
                ...filters,
                featured: e.target.checked
              })}
              aria-label="Show only featured plumbers"
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700 group-hover:text-gray-900 flex items-center">
              Featured Only
              <Award className="w-4 h-4 ml-1 text-yellow-500" />
            </span>
          </label>
        </div>

        {/* Sort By */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Sort By
          </label>
          <select
            value={safeFilters.sortBy}
            onChange={(e) => onFilterChange({
              ...filters,
              sortBy: e.target.value as SearchFilters['sortBy']
            })}
            aria-label="Sort results by"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-gray-700"
          >
            <option value="">Best Match</option>
            <option value="rating">Highest Rated</option>
            <option value="experience">Most Experienced</option>
            <option value="featured">Featured First</option>
          </select>
        </div>

        {/* Clear Filters */}
        {(safeFilters.services.length || safeFilters.rating || safeFilters.verified || safeFilters.featured || safeFilters.sortBy) && (
          <button
            onClick={() => onFilterChange({})}
            aria-label="Clear all filters"
            className="w-full px-4 py-2 text-sm text-blue-600 hover:text-blue-700 border border-blue-600 hover:border-blue-700 rounded-md transition-colors"
          >
            Clear All Filters
          </button>
        )}
      </div>
    </aside>
  );
}